import { Manifest, LanguageInfo, StyleInfo } from '../types/manifest';
import { EMERGENCY_CONFIG } from '../constants/emergencyConfig';
import { MANIFEST_RETRY_COUNT, MANIFEST_RETRY_BASE_DELAY } from '../constants/defaults';
import { validateManifest } from '../utils/validators';

export class ManifestService {
  private manifest: Manifest = EMERGENCY_CONFIG;
  private _isEmergencyMode: boolean = false;

  async load(): Promise<Manifest> {
    for (let attempt = 1; attempt <= MANIFEST_RETRY_COUNT; attempt++) {
      try {
        const response = await fetch('/audio/manifest.json', {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!validateManifest(data)) {
          throw new Error('Manifest validation failed');
        }

        this.manifest = data as Manifest;
        this._isEmergencyMode = false;
        return this.manifest;
      } catch (error) {
        console.warn(
          `[ManifestService] Load attempt ${attempt}/${MANIFEST_RETRY_COUNT} failed:`,
          error
        );

        if (attempt < MANIFEST_RETRY_COUNT) {
          await this.delay(MANIFEST_RETRY_BASE_DELAY * attempt);
        }
      }
    }

    // All retries failed -> emergency mode
    console.error('[ManifestService] All retries exhausted. Using emergency config.');
    this.manifest = EMERGENCY_CONFIG;
    this._isEmergencyMode = true;
    return this.manifest;
  }

  getManifest(): Manifest {
    return this.manifest;
  }

  isEmergencyMode(): boolean {
    return this._isEmergencyMode;
  }

  async retryLoad(): Promise<boolean> {
    try {
      const response = await fetch('/audio/manifest.json', {
        cache: 'no-cache',
      });
      if (!response.ok) return false;

      const data = await response.json();
      if (!validateManifest(data)) return false;

      this.manifest = data as Manifest;
      this._isEmergencyMode = false;
      return true;
    } catch {
      return false;
    }
  }

  getLanguages(): LanguageInfo[] {
    return Object.entries(this.manifest.languages).map(([code, entry]) => ({
      code,
      name: entry.name,
      nativeName: entry.nativeName,
    }));
  }

  getStyles(lang: string): StyleInfo[] {
    const langEntry = this.manifest.languages[lang];
    if (!langEntry) return [];
    return Object.entries(langEntry.styles).map(([key, entry]) => ({
      key,
      name: entry.name,
      description: entry.description,
      files: entry.files,
      previewUrl: entry.previewUrl,
    }));
  }

  getFileCount(lang: string, style: string): number {
    const langEntry = this.manifest.languages[lang];
    if (!langEntry) return 0;
    const styleEntry = langEntry.styles[style];
    if (!styleEntry) return 0;
    return styleEntry.files;
  }

  getAudioBaseUrl(): string {
    const url = this.manifest.audioBaseUrl;
    if (url && url.trim() !== '') {
      return url.replace(/\/+$/, '');
    }
    return '/audio';
  }

  getPreviewUrl(lang: string, style: string): string | null {
    const langEntry = this.manifest.languages[lang];
    if (!langEntry) return null;
    const styleEntry = langEntry.styles[style];
    if (!styleEntry) return null;
    return styleEntry.previewUrl;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
